import React, { useEffect, useState } from "react";
import { Pencil, Plus } from 'lucide-react';
import Sidebar1 from "components/Sidebar1";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchWithToken, updateWithToken } from "api/ApiHandler";

const CURRENT_MONTH = new Date().toLocaleString("en-US", { month: "long" });
const CURRENT_YEAR = String(new Date().getFullYear());

const DEFAULT_COLORS = {
    super98Color:   "#0897D3",
    special95Color: "#00953B",
    eplus91Color:   "#E2211C",
    dieselColor:    "#FEA30C",
};

const FUEL_FIELDS = [
    { key: "super98",   colorKey: "super98Color",   label: "Super 98"   },
    { key: "special95", colorKey: "special95Color", label: "Special 95" },
    { key: "eplus91",   colorKey: "eplus91Color",   label: "E Plus 91"  },
    { key: "diesel",    colorKey: "dieselColor",    label: "Diesel"     },
];

const EMPTY_ADD_FORM = {
    month: "",
    year: "",
    super98: "",
    special95: "",
    eplus91: "",
    diesel: "",
    super98Color:   DEFAULT_COLORS.super98Color,
    special95Color: DEFAULT_COLORS.special95Color,
    eplus91Color:   DEFAULT_COLORS.eplus91Color,
    dieselColor:    DEFAULT_COLORS.dieselColor,
};

const normalizeFuelItem = (item) => ({
    id: item?.sourceId || item?._id || item?.id || null,
    name: item?.name || "",
    nameAr: item?.nameAr || "",
    price: item?.price != null ? String(item.price) : "",
    month: item?.month || "",
    year: item?.year || "",
    color: item?.color || "#000000",
});

const getRowKey = (item) => `${item?.id || ""}-${item?.name || ""}`;

const getFuelField = (name) => FUEL_FIELDS.find((field) => field.label.toUpperCase() === String(name || "").toUpperCase());

const getFuelByField = (list, fieldKey) => list.find((item) => getFuelField(item.name)?.key === fieldKey);

const buildFullFuelPayload = (list, overrides = {}) => {
    const firstRecord = list[0] || {};

    return {
        month: String(overrides.month || firstRecord.month || CURRENT_MONTH).trim(),
        year: String(overrides.year || firstRecord.year || CURRENT_YEAR).trim(),
        super98: String(overrides.super98 ?? getFuelByField(list, "super98")?.price ?? "").trim(),
        special95: String(overrides.special95 ?? getFuelByField(list, "special95")?.price ?? "").trim(),
        eplus91: String(overrides.eplus91 ?? getFuelByField(list, "eplus91")?.price ?? "").trim(),
        diesel: String(overrides.diesel ?? getFuelByField(list, "diesel")?.price ?? "").trim(),
        super98Color: String(overrides.super98Color ?? getFuelByField(list, "super98")?.color ?? DEFAULT_COLORS.super98Color).trim(),
        special95Color: String(overrides.special95Color ?? getFuelByField(list, "special95")?.color ?? DEFAULT_COLORS.special95Color).trim(),
        eplus91Color: String(overrides.eplus91Color ?? getFuelByField(list, "eplus91")?.color ?? DEFAULT_COLORS.eplus91Color).trim(),
        dieselColor: String(overrides.dieselColor ?? getFuelByField(list, "diesel")?.color ?? DEFAULT_COLORS.dieselColor).trim(),
    };
};

export default function AdminFuelPricesPage() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [fuelList, setFuelList]           = useState([]);
    const [selectedItem, setSelectedItem]   = useState(null);
    const [isAddingNew, setIsAddingNew]     = useState(false);
    const [addForm, setAddForm]             = useState(EMPTY_ADD_FORM);
    const [isSaving, setIsSaving]           = useState(false);
    const [isLoading, setIsLoading]         = useState(true);
    const [fetchError, setFetchError]       = useState("");

    const loadFuelPrices = async () => {
        try {
            setIsLoading(true);
            setFetchError("");
            const response = await fetchWithToken("/currentFuel/getFuelPrice");
            const normalizedList = Array.isArray(response?.getPrice)
                ? response.getPrice.map(normalizeFuelItem)
                : [];
            setFuelList(normalizedList);
            setSelectedItem(normalizedList[0] || null);
        } catch (error) {
            console.error("Failed to load fuel prices:", error);
            setFetchError("Unable to load current fuel prices.");
            setFuelList([]);
            setSelectedItem(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadFuelPrices();
    }, []);

    // Edit mode change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setSelectedItem(prev => ({ ...prev, [name]: value }));
    };

    // Add mode change
    const handleAddFormChange = (e) => {
        const { name, value } = e.target;
        setAddForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddNewClick = () => {
        setIsAddingNew(true);
        const baseMonth = selectedItem?.month || fuelList[0]?.month || CURRENT_MONTH;
        const baseYear = selectedItem?.year || fuelList[0]?.year || CURRENT_YEAR;
        setAddForm({
            ...EMPTY_ADD_FORM,
            month: baseMonth,
            year: baseYear,
        });
        setSelectedItem(null);
    };

    // Save new record
    const handleSaveNew = () => {
        const requiredFields = [
            addForm.month,
            addForm.year,
            addForm.super98,
            addForm.special95,
            addForm.eplus91,
            addForm.diesel,
        ];

        if (requiredFields.some((value) => !String(value || "").trim())) {
            toast.error("Please fill Month, Year, all prices, and colors");
            return;
        }
        setIsSaving(true);
        updateWithToken("/currentFuel/createFuelPrice", [buildFullFuelPayload(fuelList, addForm)]).then(() => {
            toast.success("Fuel record added successfully!");
            setIsAddingNew(false);
            setAddForm(EMPTY_ADD_FORM);
            loadFuelPrices();
        }).catch((error) => {
            console.error("Failed to create fuel price:", error);
            toast.error("Failed to add. Try again.");
        }).finally(() => setIsSaving(false));
    };

    // Update existing
    const handleSaveEdit = () => {
        if (!selectedItem?.price) {
            toast.error("Please fill Price");
            return;
        }
        setIsSaving(true);
        const currentField = getFuelField(selectedItem.name);

        updateWithToken("/currentFuel/updateFuelPrice", {
            id: selectedItem.id,
            ...buildFullFuelPayload(fuelList, currentField ? {
                [currentField.key]: selectedItem.price,
                [currentField.colorKey]: selectedItem.color,
                month: selectedItem.month,
                year: selectedItem.year,
            } : {
                month: selectedItem.month,
                year: selectedItem.year,
            }),
        }).then(() => {
            toast.success("Fuel details updated!");
            loadFuelPrices();
        }).catch((error) => {
            console.error("Failed to update fuel price:", error);
            toast.error("Failed to update. Try again.");
        }).finally(() => setIsSaving(false));
    };

    const handleDelete = () => {
        const idToDelete = selectedItem?.id;
        const updatedList = fuelList.filter(item => item.id !== idToDelete);
        setFuelList(updatedList);
        toast.info("Fuel type removed");
        if (updatedList.length > 0) {
            setSelectedItem(updatedList[0]);
            setIsAddingNew(false);
        } else {
            handleAddNewClick();
        }
    };

    const inputStyle = {
        fontFamily: 'NeueMontreal',
        backgroundColor: '#F6F6F6',
        borderColor: 'transparent',
        borderRadius: '16px',
        padding: '16px',
        fontSize: '14px',
        transition: 'border-color 0.3s ease',
    };

    return (
        <div className="flex h-screen bg-[#F8F9FA] overflow-hidden font-sans">
            <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />

            <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Header */}
                <header className="flex items-center justify-between p-5 lg:px-5" style={{paddingLeft:10}}>
                    <h1 style={{ fontFamily: 'NeueMontreal' }} className="text-2xl font-semibold text-gray-900">
                        Current Fuel Prices
                    </h1>
                    {/* <div className="w-10 h-10 rounded-full overflow-hidden border">
                        <img src="https://i.pravatar.cc/150?u=admin" alt="Admin" className="w-full h-full object-cover" />
                    </div> */}
                </header>

                <div className="flex-1 flex gap-8 px-4 lg:px-5 pb-10 overflow-hidden" style={{paddingLeft:10}}>

                    {/* LEFT: List */}
                    <div className="w-1/3 flex flex-col gap-4">
                        <button
                            onClick={handleAddNewClick}
                            style={{ color: 'white', fontFamily: 'NeueMontreal', backgroundColor: '#EA352B', padding: '16px', borderRadius: '24px', fontWeight: 'medium', fontSize: '14px', boxShadow: '0px 4px 6px rgba(234, 53, 43, 0.2)', transition: 'background-color 0.3s ease' }}
                            className="flex items-center justify-center gap-2"
                        >
                            <Plus size={18} /> Add New Fuel
                        </button>

                        <div style={{ background: 'white', boxShadow: '0px 0px 3px 0px #c3c3c3d1' }} className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden flex-1 overflow-y-auto">
                            {isLoading ? (
                                <div className="p-10 text-center text-gray-400 text-sm">Loading...</div>
                            ) : fetchError ? (
                                <div className="p-10 text-center text-red-500 text-sm">{fetchError}</div>
                            ) : fuelList.length === 0 ? (
                                <div className="p-10 text-center text-gray-400 text-sm">No fuels added yet.</div>
                            ) : fuelList.map((item) => (
                                <div
                                    key={getRowKey(item)}
                                    onClick={() => { setSelectedItem(item); setIsAddingNew(false); }}
                                    className={`flex items-center justify-between p-5 border-b border-gray-50 cursor-pointer transition-colors ${selectedItem?.name === item.name && selectedItem?.id === item.id && !isAddingNew ? '' : 'hover:bg-gray-50/50'}`}
                                >
                                    <span className="font-medium text-gray-800 uppercase tracking-wide text-sm">{item.name}</span>
                                    <Pencil size={14} className="text-gray-400" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: Form */}
                    <div style={{ background: 'white', boxShadow: '0px 0px 3px 0px #c3c3c3d1', fontFamily: 'NeueMontreal' }} className="flex-1 bg-white rounded-[32px] border border-gray-100 shadow-sm p-8 overflow-y-auto relative">

                        {/* ── ADD NEW MODE ── */}
                        {isAddingNew ? (
                            <>
                                <div className="flex justify-between items-center mb-8">
                                    <h2 style={{ fontFamily: 'NeueMontreal', color: 'black' }} className="text-lg font-bold">
                                        Create New Month
                                    </h2>
                                </div>

                                <div className="space-y-6">
                                    {/* Month */}
                                    <div>
                                        <label className="block text-black text-md mb-2 font-light">Month</label>
                                        <input
                                            style={inputStyle}
                                            type="text"
                                            name="month"
                                            value={addForm.month}
                                            onChange={handleAddFormChange}
                                            placeholder="e.g. August"
                                            className="w-full bg-[#F6F6F6] border border-transparent rounded-xl p-4 text-sm focus:border-gray-200 outline-none transition"
                                        />
                                    </div>

                                    {/* Year */}
                                    <div>
                                        <label className="block text-black text-md mb-2 font-light">Year</label>
                                        <input
                                            style={inputStyle}
                                            type="text"
                                            name="year"
                                            value={addForm.year}
                                            onChange={handleAddFormChange}
                                            placeholder="e.g. 2026"
                                            className="w-full bg-[#F6F6F6] border border-transparent rounded-xl p-4 text-sm focus:border-gray-200 outline-none transition"
                                        />
                                    </div>

                                    {/* All 4 Fuel Fields */}
                                    {FUEL_FIELDS.map((field) => (
                                        <div key={field.key} className="grid grid-cols-[1fr_180px] gap-4">
                                            <div>
                                                <label className="block text-black text-md mb-2 font-light">{field.label} Price</label>
                                                <input
                                                    style={inputStyle}
                                                    type="number"
                                                    step="0.01"
                                                    name={field.key}
                                                    value={addForm[field.key]}
                                                    onChange={handleAddFormChange}
                                                    placeholder="0.00"
                                                    className="w-full bg-[#FBFBFB] border border-transparent rounded-xl p-4 text-sm focus:border-gray-200 outline-none transition"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-black text-md mb-2 font-light">{field.label} Color</label>
                                                <input
                                                    style={{ ...inputStyle, padding: '8px' }}
                                                    type="color"
                                                    name={field.colorKey}
                                                    value={addForm[field.colorKey]}
                                                    onChange={handleAddFormChange}
                                                    className="w-full h-[56px] bg-[#FBFBFB] border border-transparent rounded-xl cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {/* Save Button */}
                                    <button
                                        style={{ color: 'white', fontFamily: 'NeueMontreal', backgroundColor: '#EA352B', padding: '16px', borderRadius: '24px', fontWeight: 'medium', fontSize: '14px', boxShadow: '0px 4px 6px rgba(234, 53, 43, 0.2)', transition: 'background-color 0.3s ease' }}
                                        onClick={handleSaveNew}
                                        disabled={isSaving}
                                        className="w-full py-4 rounded-2xl text-sm mt-4 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isSaving ? "Saving..." : "Save New Record"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* ── EDIT MODE ── */
                            <>
                                <div className="flex justify-between items-center mb-8">
                                    <h2 style={{ fontFamily: 'NeueMontreal', color: 'black' }} className="text-lg font-bold">
                                        Modify Details
                                    </h2>
                                    {selectedItem?.id && (
                                        <button
                                            onClick={handleDelete}
                                            className="bg-[#EA352B]/10 text-[#EA352B] hover:bg-[#EA352B] hover:text-white px-5 py-2 rounded-lg text-xs font-bold transition-all"
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-black text-md mb-2 font-light">Title (English)</label>
                                        <input
                                            style={inputStyle}
                                            type="text"
                                            name="name"
                                            value={selectedItem?.name || ""}
                                            onChange={handleChange}
                                            className="w-full bg-[#F6F6F6] border border-transparent rounded-xl p-4 text-sm focus:border-gray-200 outline-none transition"
                                        />
                                    </div>

                                    {/* <div className="text-right">
                                        <label className="block text-black text-md mb-2 font-light">عنوان (العربية)</label>
                                        <input
                                            style={inputStyle}
                                            type="text"
                                            name="nameAr"
                                            dir="rtl"
                                            value={selectedItem?.nameAr || ""}
                                            onChange={handleChange}
                                            className="w-full bg-[#F6F6F6] border border-transparent rounded-xl p-4 text-sm text-right focus:border-gray-200 outline-none transition"
                                        />
                                    </div> */}

                                    <div>
                                        <label className="block text-black text-md mb-2 font-light">Price</label>
                                        <div className="relative">
                                            <input
                                                style={inputStyle}
                                                type="number"
                                                name="price"
                                                value={selectedItem?.price || ""}
                                                onChange={handleChange}
                                                className="w-full bg-[#FBFBFB] border border-transparent rounded-xl p-4 text-sm focus:border-gray-200 outline-none transition"
                                            />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">AED</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-black text-md mb-2 font-light">Month</label>
                                            <input
                                                style={inputStyle}
                                                type="text"
                                                name="month"
                                                value={selectedItem?.month || ""}
                                                readOnly
                                                className="w-full bg-[#FBFBFB] border border-transparent rounded-xl p-4 text-sm outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-black text-md mb-2 font-light">Year</label>
                                            <input
                                                style={inputStyle}
                                                type="text"
                                                name="year"
                                                value={selectedItem?.year || ""}
                                                readOnly
                                                className="w-full bg-[#FBFBFB] border border-transparent rounded-xl p-4 text-sm outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-black text-md mb-2 font-light">Color Code</label>
                                        <div className="relative">
                                            <input
                                                style={inputStyle}
                                                type="text"
                                                name="color"
                                                value={selectedItem?.color || ""}
                                                onChange={handleChange}
                                                className="w-full bg-[#FBFBFB] border border-transparent rounded-xl p-4 text-sm focus:border-gray-200 outline-none uppercase"
                                            />
                                            <div
                                                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-white shadow-md"
                                                style={{ backgroundColor: selectedItem?.color }}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        style={{ color: 'white', fontFamily: 'NeueMontreal', backgroundColor: '#EA352B', padding: '16px', borderRadius: '24px', fontWeight: 'medium', fontSize: '14px', boxShadow: '0px 4px 6px rgba(234, 53, 43, 0.2)', transition: 'background-color 0.3s ease' }}
                                        disabled={isSaving || !selectedItem}
                                        onClick={handleSaveEdit}
                                        className="w-full py-4 rounded-2xl text-sm mt-4 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {isSaving ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}